// skills/pilfering/pilfering.js
// Pilfering skill logic

import {
    TARGETS,
    GLOVES,
    GUILD_REQUIREMENTS,
    PILFERER_UNLOCK_LEVELS,
    GUILD_CHEST_CONFIG,
    PILFERER_SPEED_CONFIG,
    PILFERER_XP_CONFIG,
    CONTRACT_ASSIST_CONFIG,
    GUILD_DOMAINS,
    getDomainItemsAtTier
} from './pilferingData.js';
import { gameState } from '../../game/gameState.js';
import { addXP } from '../../systems/experience.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { awardDrops, canFitItem } from '../../systems/drops.js';
import { showItemGain } from '../../ui/floatingNotifications.js';
import { getZone } from '../combat/combatData.js';
import { rollForCorruption } from '../../systems/corruption.js';
import { getItem } from '../../data/items/index.js';
import { getTalentFlatBonus, getTalentLootBonus, rollTalentProc } from '../../systems/talentTree/talentTree.js';
import { toastSuccess } from '../../ui/toast.js';
import { architectingSkill } from '../architecting/architecting.js';
import { recordTalentProc } from '../../ui/talentActivity.js';
import { BaseSkill } from '../../core/BaseSkill.js';

/**
 * Roll for Golden Opportunity talent - bonus gold when claiming chests
 * @param {Array<{itemId: string, quantity: number}>} items - Items being claimed
 * @returns {number} - Bonus gold awarded (0 if not proc'd)
 */
export function rollGoldenOpportunity(items) {
    if (!items || items.length === 0) return 0;

    // Check if talent is unlocked
    if (!rollTalentProc(SKILLS.PILFERING, 'goldenOpportunity')) {
        return 0;
    }

    // Calculate total sell value of claimed items
    let totalValue = 0;
    for (const item of items) {
        const itemData = getItem(item.itemId);
        if (itemData?.value) {
            totalValue += itemData.value * item.quantity;
        }
    }

    if (totalValue <= 0) return 0;

    // Golden Opportunity: 25% of items' sell value as bonus gold
    const bonusGold = Math.floor(totalValue * 0.25);
    if (bonusGold > 0) {
        gameState.addGold(bonusGold);
        recordTalentProc(SKILLS.PILFERING, 'Golden Opportunity', `+${bonusGold} gold`, null);
    }

    return bonusGold;
}

class PilferingSkill extends BaseSkill {
    constructor() {
        super(SKILLS.PILFERING, GLOVES, 'TREATED_HIDE');

        // Guild callbacks
        this.onGuildGather = null;      // (domainId, itemId, itemName) => void
        this.onContractAssist = null;   // (contractId, reduction) => void
        this.onGuildUnlock = null;      // () => void
        this.onPilfererUnlock = null;   // (count) => void
    }

    /**
     * Get the currently equipped gloves object (alias for BaseSkill method)
     */
    get currentGloves() {
        return this.getCurrentTool();
    }

    canPickpocket(targetId) {
        // Find target by id property, not by object key
        const target = Object.values(TARGETS).find(t => t.id === targetId);
        if (!target) return { success: false, reason: 'Target not found' };

        const skill = gameState.getSkill(SKILLS.PILFERING);
        if (skill.level < target.level) {
            return { success: false, reason: `Requires level ${target.level} Pilfering` };
        }

        // Check if output can fit in inventory
        const outputItem = target.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    startPickpocketing(targetId) {
        // Find target by id property, not by object key
        const target = Object.values(TARGETS).find(t => t.id === targetId);
        const canPickpocket = this.canPickpocket(targetId);

        if (!canPickpocket.success) {
            logger.debug('Cannot pickpocket:', canPickpocket.reason);
            return false;
        }

        const baseTime = target.baseTime;
        const speedMultiplier = this.getEffectiveSpeedMultiplier();
        const pickpocketTime = baseTime / speedMultiplier;

        const action = {
            type: 'pilfering',
            name: `Pickpocketing ${target.name}`,
            target: target,
            targetId: targetId.toUpperCase(),
            progress: 0,
            duration: pickpocketTime,
            startTime: Date.now(),
            forceComplete: false,
            vanishingActChecked: false, // Track if we've checked for instant complete

            // Tick function called every game tick
            // speedMultiplier param allows admin panel to speed up actions
            tick: (speedMultiplier = 1) => {
                // Check for force complete (used by admin panel)
                if (action.forceComplete) {
                    action.forceComplete = false;
                    this.completePickpocket(target);
                    const canContinue = this.canPickpocket(targetId);
                    if (canContinue.success) {
                        this.startPickpocketing(targetId);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                // Vanishing Act - 5% chance to instantly complete (check once per action)
                if (!action.vanishingActChecked) {
                    action.vanishingActChecked = true;
                    if (rollTalentProc(SKILLS.PILFERING, 'vanishingAct')) {
                        recordTalentProc(SKILLS.PILFERING, 'Vanishing Act', 'Instant pickpocket!', null);
                        this.completePickpocket(target);
                        const canContinue = this.canPickpocket(targetId);
                        if (canContinue.success) {
                            this.startPickpocketing(targetId);
                        } else {
                            gameState.clearAction();
                        }
                        return;
                    }
                }

                // Apply speed multiplier to elapsed time
                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                if (action.progress >= 1) {
                    this.completePickpocket(target);
                    const canContinue = this.canPickpocket(targetId);
                    if (canContinue.success) {
                        this.startPickpocketing(targetId);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        return true;
    }

    /**
     * Roll for pilfering talent procs
     * Returns object with proc results
     */
    rollPilferingProcs() {
        const procs = {
            shadowClone: false,
            phantomStrike: false,
            tenXCoins: false,
            bonusLootFind: false
        };

        // tenXCoins (3% for 10x) - check first, highest priority
        if (rollTalentProc(SKILLS.PILFERING, 'tenXCoins')) {
            procs.tenXCoins = true;
        }
        // Phantom Strike (5% for 5x) - only if not 10x
        else if (rollTalentProc(SKILLS.PILFERING, 'phantomStrike')) {
            procs.phantomStrike = true;
        }

        // Shadow Clone (5% for double action) - independent, doubles ALL
        if (rollTalentProc(SKILLS.PILFERING, 'shadowClone')) {
            procs.shadowClone = true;
        }

        // Bonus Loot Find (20% for extra drops from combat/pilfering) - independent
        if (rollTalentProc(SKILLS.PILFERING, 'bonusLootFind')) {
            procs.bonusLootFind = true;
        }

        return procs;
    }

    completePickpocket(target) {
        // Get output item ID for proc notifications
        const outputItemId = target.drops[0]?.itemId;

        // Pass target.id for admin node XP override
        const xpResult = addXP(SKILLS.PILFERING, target.xp, target.id);

        // Roll for talent procs
        const procs = this.rollPilferingProcs();

        // Calculate coin multiplier from procs (highest wins)
        let coinMultiplier = 1;
        if (procs.tenXCoins) {
            coinMultiplier = 5;  // Reduced from 10x
        } else if (procs.phantomStrike) {
            coinMultiplier = 2;  // Reduced from 5x
        }

        // Shadow Clone doubles the action (XP and drops)
        if (procs.shadowClone) {
            coinMultiplier *= 2;
            // Award bonus XP for the clone action
            addXP(SKILLS.PILFERING, target.xp, target.id);
        }

        // Apply loot bonus from talents
        const lootBonus = getTalentLootBonus(SKILLS.PILFERING);
        if (lootBonus > 0) {
            coinMultiplier *= (1 + lootBonus);
        }

        // Create modified drops with multiplier
        const modifiedDrops = target.drops.map(drop => ({
            ...drop,
            quantity: Math.floor(drop.quantity * coinMultiplier)
        }));

        // Award drops (handles corruption loot multiplier centrally)
        awardDrops(SKILLS.PILFERING, modifiedDrops);

        // Record proc notifications after drops awarded
        if (procs.tenXCoins && outputItemId) {
            recordTalentProc(SKILLS.PILFERING, 'Master Thief', null, null, { itemId: outputItemId, quantity: modifiedDrops[0]?.quantity || 10 });
        } else if (procs.phantomStrike && outputItemId) {
            recordTalentProc(SKILLS.PILFERING, 'Phantom Strike', null, null, { itemId: outputItemId, quantity: modifiedDrops[0]?.quantity || 5 });
        }

        if (procs.shadowClone) {
            recordTalentProc(SKILLS.PILFERING, 'Shadow Clone', '2x action!', null);
        }

        // Bonus Loot Find proc - 20% chance for extra bonus drops
        if (procs.bonusLootFind) {
            // Award a bonus drop of the same type (smaller amount)
            const bonusDrops = target.drops.map(drop => ({
                ...drop,
                quantity: Math.max(1, Math.floor(drop.quantity * 0.5))
            }));
            awardDrops(SKILLS.PILFERING, bonusDrops);
            if (outputItemId) {
                recordTalentProc(SKILLS.PILFERING, 'Bonus Loot', null, null, { itemId: outputItemId, quantity: bonusDrops[0]?.quantity || 1 });
            }
        }

        // Roll for corruption proc
        rollForCorruption(SKILLS.PILFERING);

        this.onPickpocketComplete(target, xpResult);
    }

    stopPickpocketing() {
        gameState.clearAction();
    }

    /**
     * Purchase or equip gloves
     */
    upgradeGloves(glovesId) {
        const gloves = Object.values(GLOVES).find(g => g.id === glovesId);
        if (!gloves) return { success: false, reason: 'Invalid gloves' };

        const skill = gameState.getSkill(SKILLS.PILFERING);
        if (skill.level < gloves.level) {
            return { success: false, reason: `Requires level ${gloves.level} Pilfering` };
        }

        // Check if already owned
        if (gameState.hasPurchasedTool(SKILLS.PILFERING, glovesId)) {
            // Just equip it
            gameState.equipTool(SKILLS.PILFERING, glovesId);
            return { success: true, action: 'equipped' };
        }

        // Try to purchase
        const purchaseResult = gameState.purchaseTool(SKILLS.PILFERING, glovesId, gloves.price);
        if (!purchaseResult.success) {
            return purchaseResult;
        }

        // Auto-equip after purchase
        gameState.equipTool(SKILLS.PILFERING, glovesId);
        return { success: true, action: 'purchased' };
    }

    getAvailableTargets() {
        const skill = gameState.getSkill(SKILLS.PILFERING);
        return Object.values(TARGETS).filter(target => target.level <= skill.level);
    }

    onPickpocketComplete(target, xpResult) {
        logger.game(`Pickpocketed ${target.name}! +${target.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Pilfering level up! Now level ${xpResult.newLevel}`);
            // Check for pilferer slot unlocks on level up
            this.checkPilfererUnlocks();
        }
    }

    // ==================== GUILD METHODS ====================

    /**
     * Check if player can unlock the pilfering guild
     * Requires: Guild Hall milestone + Pilfering level 30 + 40,000 gold
     * @returns {{ success: boolean, reason?: string, requirements?: Object }}
     */
    canUnlockGuild() {
        if (gameState.isGuildUnlocked()) {
            return { success: false, reason: 'Guild already unlocked' };
        }

        const pilferingLevel = gameState.getSkill(SKILLS.PILFERING)?.level || 1;
        const hasGuildHall = gameState.hasGuildHallMilestone();
        const goldCount = gameState.getGold();

        const requirements = {
            guildHall: { required: true, current: hasGuildHall, met: hasGuildHall },
            pilfering: { required: GUILD_REQUIREMENTS.pilfering, current: pilferingLevel, met: pilferingLevel >= GUILD_REQUIREMENTS.pilfering },
            gold: { required: GUILD_REQUIREMENTS.gold, current: goldCount, met: goldCount >= GUILD_REQUIREMENTS.gold }
        };

        const allMet = requirements.guildHall.met && requirements.pilfering.met && requirements.gold.met;

        if (!allMet) {
            const missing = [];
            if (!requirements.guildHall.met) missing.push('Build Guild Hall (Architecting Lv20)');
            if (!requirements.pilfering.met) missing.push(`Pilfering ${GUILD_REQUIREMENTS.pilfering}`);
            if (!requirements.gold.met) missing.push(`${GUILD_REQUIREMENTS.gold.toLocaleString()} Gold`);
            return { success: false, reason: `Requires: ${missing.join(', ')}`, requirements };
        }

        return { success: true, requirements };
    }

    /**
     * Unlock the pilfering guild
     * @returns {{ success: boolean, reason?: string }}
     */
    unlockGuild() {
        const canUnlock = this.canUnlockGuild();
        if (!canUnlock.success) {
            return canUnlock;
        }

        // Deduct gold cost
        gameState.removeItem('coins', GUILD_REQUIREMENTS.gold);

        gameState.unlockGuild();
        logger.game('Pilfering Guild unlocked!');

        // Sync pilferer unlocks based on current level
        this.checkPilfererUnlocks();

        if (this.onGuildUnlock) {
            this.onGuildUnlock();
        }

        return { success: true };
    }

    /**
     * Check and unlock pilferer slots based on pilfering level
     */
    checkPilfererUnlocks() {
        if (!gameState.isGuildUnlocked()) return;

        const pilferingLevel = gameState.getSkill(SKILLS.PILFERING)?.level || 1;
        let currentUnlocked = gameState.getUnlockedPilfererCount();

        // Check how many should be unlocked at this level
        let shouldBeUnlocked = 0;
        for (const level of PILFERER_UNLOCK_LEVELS) {
            if (pilferingLevel >= level) {
                shouldBeUnlocked++;
            }
        }

        // Unlock any missing slots
        while (currentUnlocked < shouldBeUnlocked && currentUnlocked < 7) {
            const result = gameState.unlockPilfererSlot();
            if (result.success) {
                currentUnlocked = result.newCount;  // Update count to prevent infinite loop
                logger.game(`New pilferer slot unlocked! (${result.newCount}/7)`);
                if (this.onPilfererUnlock) {
                    this.onPilfererUnlock(result.newCount);
                }
            } else {
                break;  // Exit if unlock fails
            }
        }
    }

    /**
     * Get number of unlocked pilferers based on current pilfering level
     * @returns {number}
     */
    getUnlockedPilfererCount() {
        const pilferingLevel = gameState.getSkill(SKILLS.PILFERING)?.level || 1;
        let count = 0;
        for (const level of PILFERER_UNLOCK_LEVELS) {
            if (pilferingLevel >= level) {
                count++;
            }
        }
        return count;
    }

    /**
     * Get number of currently active pilferers
     * @returns {number}
     */
    getActivePilfererCount() {
        return gameState.getActivePilfererCount();
    }

    /**
     * Get a domain by ID
     * @param {string} domainId
     * @returns {Object|null}
     */
    getDomain(domainId) {
        return Object.values(GUILD_DOMAINS).find(d => d.id === domainId) || null;
    }

    /**
     * Get all domains that have active pilferers assigned
     * @returns {Array} Array of domain objects
     */
    getActiveDomains() {
        const pilferers = gameState.getActivePilferers();
        const domains = [];
        for (const pilferer of pilferers) {
            const domain = this.getDomain(pilferer.domainId);
            if (domain && !domains.includes(domain)) {
                domains.push(domain);
            }
        }
        return domains;
    }

    /**
     * Activate a pilferer on a domain
     * @param {string} domainId
     * @returns {{ success: boolean, reason?: string }}
     */
    activatePilferer(domainId) {
        if (!gameState.isGuildUnlocked()) {
            return { success: false, reason: 'Guild not unlocked' };
        }

        const domain = this.getDomain(domainId);
        if (!domain) {
            return { success: false, reason: 'Invalid domain' };
        }

        return gameState.startPilferer(domainId);
    }

    /**
     * Deactivate a pilferer
     * @param {string} domainId
     * @returns {{ success: boolean, reason?: string }}
     */
    deactivatePilferer(domainId) {
        return gameState.stopPilferer(domainId);
    }

    /**
     * Calculate effective gather time for pilferers
     * @param {number} pilferingLevel
     * @param {boolean} chestFull - Whether guild chest is full
     * @returns {number} Time in ms
     */
    getGatherDuration(pilferingLevel, chestFull = false) {
        // Calculate slowdown: 50% at level 1, 25% at level 99
        const slowdownRange = PILFERER_SPEED_CONFIG.BASE_SLOWDOWN_PERCENT - PILFERER_SPEED_CONFIG.MIN_SLOWDOWN_PERCENT;
        const slowdownPercent = PILFERER_SPEED_CONFIG.BASE_SLOWDOWN_PERCENT - (pilferingLevel * slowdownRange / 99);

        let effectiveTime = PILFERER_SPEED_CONFIG.BASE_GATHER_TIME * (1 + slowdownPercent / 100);

        // Apply talent pilferer speed bonus (Guild Connections)
        const pilfererSpeedBonus = getTalentFlatBonus(SKILLS.PILFERING, 'pilfererSpeed');
        if (pilfererSpeedBonus > 0) {
            effectiveTime /= (1 + pilfererSpeedBonus);
        }

        // Apply Guild Vault milestone speed bonus (+2% per tier, up to 6%)
        const milestoneSpeedMult = gameState.getPilfererSpeedMultiplier();
        effectiveTime /= milestoneSpeedMult;

        // Apply chest full penalty if at 99 architecting
        if (chestFull) {
            const archLevel = gameState.getSkill(SKILLS.ARCHITECTING)?.level || 1;
            if (archLevel >= 99) {
                effectiveTime *= (1 + GUILD_CHEST_CONFIG.FULL_CHEST_SLOWDOWN);
            }
        }

        return effectiveTime;
    }

    /**
     * Get contract assist proc chance based on level
     * @param {number} pilferingLevel
     * @returns {number} Chance (0-1)
     */
    getContractAssistChance(pilferingLevel) {
        const range = CONTRACT_ASSIST_CONFIG.MAX_PROC_CHANCE - CONTRACT_ASSIST_CONFIG.MIN_PROC_CHANCE;
        let baseChance = CONTRACT_ASSIST_CONFIG.MIN_PROC_CHANCE + (pilferingLevel * range / 99);

        // Apply Bounty Guild Connections talent bonus (+2% per rank)
        const guildAssistBonus = getTalentFlatBonus(SKILLS.BOUNTY, 'guildAssistBonus');
        if (guildAssistBonus > 0) {
            baseChance += guildAssistBonus;
        }

        return baseChance;
    }

    /**
     * Get contract assist reduction based on level
     * @param {number} pilferingLevel
     * @returns {number} Reduction percentage (0-1)
     */
    getContractAssistReduction(pilferingLevel) {
        const range = CONTRACT_ASSIST_CONFIG.MAX_PROGRESS_REDUCTION - CONTRACT_ASSIST_CONFIG.MIN_PROGRESS_REDUCTION;
        return CONTRACT_ASSIST_CONFIG.MIN_PROGRESS_REDUCTION + (pilferingLevel * range / 99);
    }

    /**
     * Get current pilfering tier level based on Beastmaster's Lodge milestones
     * @returns {number} Tier level (0-3)
     */
    getPilferingTierLevel() {
        let tier = 0;
        if (gameState.hasMilestone('pilferers_network_i')) tier = 1;
        if (gameState.hasMilestone('pilferers_network_ii')) tier = 2;
        if (gameState.hasMilestone('pilferers_network_iii')) tier = 3;
        return tier;
    }

    /**
     * Main tick function for guild passive gathering
     * Called from game tick loop
     * @param {number} speedMultiplier - Speed multiplier from admin panel
     */
    tickGuild(speedMultiplier = 1) {
        if (!gameState.isGuildUnlocked()) return;

        // Keep pilferer unlock count synced with level (catches load/migration cases)
        this.checkPilfererUnlocks();

        const pilferingLevel = gameState.getSkill(SKILLS.PILFERING)?.level || 1;
        const archLevel = gameState.getSkill(SKILLS.ARCHITECTING)?.level || 1;
        const now = Date.now();

        // Get active domains to check effective fullness
        const activeDomains = this.getActiveDomains();

        // Check both slot-based full AND effective full (all domain items at max stack)
        const slotFull = gameState.isGuildChestFull();
        const effectivelyFull = gameState.isChestEffectivelyFull(activeDomains);
        const chestFull = slotFull || effectivelyFull;

        // If chest is full (either way) and arch < 99, don't gather
        const canGather = !chestFull || archLevel >= 99;

        const pilferers = gameState.getActivePilferers();

        for (const pilferer of pilferers) {
            if (!pilferer.startedAt) continue;

            const domain = this.getDomain(pilferer.domainId);
            if (!domain) continue;

            // Calculate gather duration
            const gatherDuration = this.getGatherDuration(pilferingLevel, chestFull);

            // Check progress
            const elapsed = (now - pilferer.startedAt) * speedMultiplier;
            const progress = elapsed / gatherDuration;

            if (progress >= 1 && canGather) {
                this.completePilfererGather(pilferer, domain, pilferingLevel);
            }
        }

        // Contract assistance is now triggered on skill action completions
        // See tryContractAssist() which is called by skill complete handlers
    }

    /**
     * Complete a pilferer's gather and add item to chest
     * @param {Object} pilferer
     * @param {Object} domain
     * @param {number} pilferingLevel
     */
    completePilfererGather(pilferer, domain, pilferingLevel) {
        // Get tier-aware item pool for this domain
        const tierLevel = this.getPilferingTierLevel();
        const availableItems = getDomainItemsAtTier(domain.id, tierLevel);

        // Pick random item from tier-expanded pool
        const itemId = availableItems[Math.floor(Math.random() * availableItems.length)];

        // Check if auto-loot is enabled (Architect's Sanctum)
        let result;
        let destination = 'chest';

        if (gameState.hasAutoLootChests()) {
            // Try auto-loot directly to bank (seeds to BloomHold first)
            result = gameState.autoLootToBank(itemId, 1, 'pilfering');
            if (result.success && result.added > 0) {
                destination = result.destination || 'bank';
                // Show floating notification for auto-looted items
                showItemGain(itemId, result.added);
                // Roll Golden Opportunity for auto-looted items
                rollGoldenOpportunity([{ itemId, quantity: result.added }]);
            } else {
                // Fall back to guild chest if bank/bloomhold is full
                result = gameState.addGuildChestItem(itemId, 1);
                destination = 'chest';
                if (result.success) {
                    showItemGain(itemId, 1);
                }
            }
        } else {
            // Add to guild chest as usual
            result = gameState.addGuildChestItem(itemId, 1);
        }

        if (result.success) {
            gameState.incrementGuildItemsGathered();

            // Award pilfering XP for commanding the guild
            const pilfererXP = this.getPilfererXP(pilferingLevel);
            addXP(SKILLS.PILFERING, pilfererXP, null, { skipCorruption: true });

            // Get item name for logging
            const item = getItem(itemId);
            const itemName = item ? item.name : itemId;

            const destLabel = destination === 'chest' ? 'chest' : destination;
            logger.debug(`[GUILD] Pilferer gathered ${itemName} from ${domain.name} → ${destLabel} (+${pilfererXP} XP)`);

            // Reset pilferer timer
            gameState.resetPilfererTimer(pilferer.domainId, itemId);

            // Trigger UI callback with destination info
            if (this.onGuildGather) {
                this.onGuildGather(pilferer.domainId, itemId, itemName, destination);
            }
        } else {
            // Storage full - reset timer anyway to prevent spam
            gameState.resetPilfererTimer(pilferer.domainId, null);
        }
    }

    /**
     * Calculate XP awarded per pilferer action
     * Scales with pilfering level: 5 XP at level 1, ~18 XP at level 99
     * @param {number} pilferingLevel
     * @returns {number} XP amount
     */
    getPilfererXP(pilferingLevel) {
        const baseXP = PILFERER_XP_CONFIG.BASE_XP;
        const scalePerLevel = PILFERER_XP_CONFIG.SCALE_PER_LEVEL;
        return Math.floor(baseXP + (pilferingLevel * scalePerLevel));
    }

    /**
     * Try contract assistance on skill action completion
     * Called by skills when they complete an action (mining a rock, cooking food, etc.)
     * This ensures the 1-5% proc chance is per action, not per game tick
     * @param {string} actionType - The type of action completed ('mining', 'fishing', 'combat', etc.)
     * @param {string} actionSubType - Optional subtype (e.g., 'ichor' or 'glyph' for glyphbinding)
     * @returns {boolean} True if assist proc'd
     */
    tryContractAssist(actionType, actionSubType = null) {
        if (!gameState.isGuildUnlocked()) return false;

        const pilferingLevel = gameState.getSkill(SKILLS.PILFERING)?.level || 1;
        const activePilferers = gameState.getActivePilferers();

        if (activePilferers.length === 0) return false;

        // Check if chest is full (slot-based or effectively full) - no assist if full and arch < 99
        const activeDomains = this.getActiveDomains();
        const slotFull = gameState.isGuildChestFull();
        const effectivelyFull = gameState.isChestEffectivelyFull(activeDomains);
        const chestFull = slotFull || effectivelyFull;
        const archLevel = gameState.getSkill(SKILLS.ARCHITECTING)?.level || 1;
        if (chestFull && archLevel < 99) return false;

        return this.checkContractAssist(pilferingLevel, activePilferers, actionType, actionSubType);
    }

    /**
     * Check for contract assistance proc
     * @param {number} pilferingLevel
     * @param {Array} activePilferers
     * @param {string} actionType - The type of action that was just completed
     * @param {string} actionSubType - Optional subtype for skills with multiple modes
     */
    checkContractAssist(pilferingLevel, activePilferers, actionType = null, actionSubType = null) {
        // Get active bounty contracts
        const bountyContracts = gameState.getBountyContracts?.() || [];
        if (bountyContracts.length === 0) return false;

        // Use the passed actionType or fall back to current action
        const effectiveActionType = actionType || gameState.getCurrentAction()?.type;
        if (!effectiveActionType) return false;

        const procChance = this.getContractAssistChance(pilferingLevel);
        let anyAssisted = false;

        for (const contract of bountyContracts) {
            // Skip contracts that are already complete (ready to deliver)
            // Progress is stored in contract.progress[itemId/mobId], not on req.current
            const isComplete = contract.requirements?.every(req => {
                const reqId = req.itemId || req.mobId;
                if (req.itemId) {
                    // For items: progress = current inventory - starting inventory
                    const currentCount = gameState.getItemCount(req.itemId);
                    const startingCount = contract.startingInventory?.[req.itemId] || 0;
                    const progress = Math.max(0, currentCount - startingCount);
                    return progress >= req.target;
                } else if (req.mobId) {
                    // For mobs: progress is stored directly
                    const progress = contract.progress?.[req.mobId] || 0;
                    return progress >= req.target;
                }
                return true;
            });
            if (isComplete) continue;

            const contractType = contract.type;

            // For kill contracts: must be doing combat with a Hunting pilferer active
            if (contractType === 'kill') {
                if (effectiveActionType !== 'combat') continue;

                // Check if current zone has the required mob (get from current action if needed)
                const currentAction = gameState.getCurrentAction();
                const zoneId = currentAction?.zoneId;
                if (!zoneId) continue;

                // Get all mobs in this zone
                const zone = getZone(zoneId);
                const zoneMobs = zone?.mobs || [];

                // Check if any contract requirement mob is in this zone
                const hasRequiredMob = contract.requirements?.some(req =>
                    req.mobId && zoneMobs.includes(req.mobId)
                );
                if (!hasRequiredMob) continue;

                // Require a Hunting domain pilferer for kill contract assists
                const hasHuntingPilferer = activePilferers.some(p => {
                    const domain = this.getDomain(p.domainId);
                    return domain?.contractCategory === 'kill';
                });
                if (!hasHuntingPilferer) continue;
            } else {
                // For gathering/processing contracts:
                // Must be doing a matching skill AND pilferer domain must include the required item

                // Map action types to contract categories
                const actionToCategory = {
                    mining: 'gathering',
                    woodcutting: 'gathering',
                    fishing: 'gathering',
                    gatherer: 'gathering',
                    smithing: 'processing',
                    cooking: 'processing',
                    crafting: 'processing',
                    woodworking: 'processing',
                    alchemy: 'processing'
                };

                // Special handling for glyphbinding which has two subtypes
                let activeCategory;
                if (effectiveActionType === 'glyphbinding') {
                    // ichor gathering = gathering, glyph crafting = processing
                    activeCategory = actionSubType === 'glyph' ? 'processing' : 'gathering';
                } else {
                    activeCategory = actionToCategory[effectiveActionType];
                }

                if (activeCategory !== contractType) continue;

                // Find a pilferer whose domain includes the contract's required item
                const requiredItems = contract.requirements?.map(r => r.itemId).filter(Boolean) || [];
                if (requiredItems.length === 0) continue;

                const matchingPilferer = activePilferers.find(p => {
                    const domain = this.getDomain(p.domainId);
                    if (!domain || domain.contractCategory !== contractType) return false;
                    // Check if this domain can provide any of the required items
                    return requiredItems.some(itemId => domain.items?.includes(itemId));
                });

                if (!matchingPilferer) continue;
            }

            // Calculate effective proc chance based on contract type
            let effectiveProcChance = procChance;
            if (contractType === 'kill') {
                // Combat Intel: +25% per rank for kill contracts
                const killContractBonus = getTalentFlatBonus(SKILLS.PILFERING, 'killContractAssist');
                if (killContractBonus > 0) {
                    effectiveProcChance *= (1 + killContractBonus);
                }
            } else {
                // Network Contacts: +10% per rank for gathering/processing contracts
                const gatherProcessBonus = getTalentFlatBonus(SKILLS.PILFERING, 'gatherProcessAssist');
                if (gatherProcessBonus > 0) {
                    effectiveProcChance *= (1 + gatherProcessBonus);
                }
            }

            // Roll for proc (1-5% chance based on level, boosted by talents)
            if (Math.random() >= effectiveProcChance) continue;

            // Calculate reduction (base 1-2% based on level)
            let reduction = this.getContractAssistReduction(pilferingLevel);

            // Underworld Kingpin: contract assists reduce 2x normal amount
            const assistMultiplier = getTalentFlatBonus(SKILLS.PILFERING, 'contractAssistMultiplier');
            if (assistMultiplier > 0) {
                reduction *= assistMultiplier;
            }

            // Apply reduction to contract requirements
            let assisted = false;
            for (const req of contract.requirements) {
                if (req.target && req.target > 1) {
                    const reductionAmount = Math.max(1, Math.floor(req.target * reduction));
                    req.target = Math.max(1, req.target - reductionAmount);
                    assisted = true;
                }
            }

            if (assisted) {
                // Track assists on the contract itself
                if (!contract.pilfererAssists) {
                    contract.pilfererAssists = 0;
                }
                contract.pilfererAssists++;

                gameState.incrementGuildContractAssists();
                logger.debug(`[GUILD] Pilferer assisted with contract! -${Math.round(reduction * 100)}%`);

                // Trigger UI callback for notification
                if (this.onContractAssist) {
                    this.onContractAssist(contract.contractId, reduction);
                }

                anyAssisted = true;
            }
        }

        return anyAssisted;
    }

    /**
     * Get pilferer progress (0-1) for a domain
     * @param {string} domainId
     * @returns {number}
     */
    getPilfererProgress(domainId) {
        const pilferer = gameState.getPilferer(domainId);
        if (!pilferer || !pilferer.active || !pilferer.startedAt) {
            return { percent: 0, itemId: null };
        }

        const pilferingLevel = gameState.getSkill(SKILLS.PILFERING)?.level || 1;

        // Check both slot-full and effectively full
        const activeDomains = this.getActiveDomains();
        const slotFull = gameState.isGuildChestFull();
        const effectivelyFull = gameState.isChestEffectivelyFull(activeDomains);
        const chestFull = slotFull || effectivelyFull;

        const gatherDuration = this.getGatherDuration(pilferingLevel, chestFull);

        const elapsed = Date.now() - pilferer.startedAt;
        const progress = Math.min(1, elapsed / gatherDuration);

        return {
            percent: progress * 100,
            itemId: pilferer.lastItemId
        };
    }

    /**
     * Claim item from guild chest to inventory
     * @param {string} itemId
     * @param {number} amount - Amount to claim (default: all)
     * @returns {{ success: boolean, claimed: number, reason?: string }}
     */
    claimFromGuildChest(itemId, amount = null) {
        const available = gameState.getGuildChestItemCount(itemId);
        if (available === 0) {
            return { success: false, claimed: 0, reason: 'Item not in chest' };
        }

        const toClaim = amount === null ? available : Math.min(amount, available);

        // Try to add to inventory
        const addResult = gameState.addItem(itemId, toClaim);
        if (!addResult.success) {
            return { success: false, claimed: 0, reason: addResult.reason || 'Inventory full' };
        }

        // Remove from chest
        gameState.removeGuildChestItem(itemId, addResult.added);

        // Award architecting XP from Guild Liaison talent (if unlocked)
        architectingSkill.awardGuildChestXP();

        const item = getItem(itemId);
        const itemName = item ? item.name : itemId;
        logger.debug(`[GUILD] Claimed ${addResult.added}x ${itemName} from guild chest`);

        return { success: true, claimed: addResult.added };
    }

    /**
     * Claim all items from guild chest to inventory
     * @returns {{ success: boolean, claimedCount: number, itemsClaimed: Array, bonusGold: number }}
     */
    claimAllFromGuildChest() {
        const chest = gameState.getGuildChest();
        const itemsClaimed = [];
        let claimedCount = 0;

        // Clone array since we're modifying it
        const itemsToProcess = [...chest];

        for (const slot of itemsToProcess) {
            if (!slot) continue;

            const result = this.claimFromGuildChest(slot.itemId, slot.quantity);
            if (result.success && result.claimed > 0) {
                itemsClaimed.push({ itemId: slot.itemId, quantity: result.claimed });
                claimedCount++;
            }
        }

        // Golden Opportunity: 10% chance for bonus gold when claiming
        const bonusGold = rollGoldenOpportunity(itemsClaimed);

        if (claimedCount > 0) {
            logger.game(`Claimed ${claimedCount} item types from guild chest`);
        }

        return { success: true, claimedCount, itemsClaimed, bonusGold };
    }

    /**
     * Get guild chest stats for UI
     * @returns {{ usedSlots: number, maxSlots: number, items: Array, isFull: boolean, isEffectivelyFull: boolean }}
     */
    getGuildChestStats() {
        const activeDomains = this.getActiveDomains();
        const slotFull = gameState.isGuildChestFull();
        const effectivelyFull = gameState.isChestEffectivelyFull(activeDomains);

        return {
            usedSlots: gameState.getGuildChestUniqueCount(),
            maxSlots: gameState.getMaxGuildChestSlots(),
            items: gameState.getGuildChest(),
            isFull: slotFull,
            isEffectivelyFull: effectivelyFull
        };
    }

    /**
     * Get number of pilferers that should be unlocked at current level
     * @returns {number}
     */
    getExpectedPilfererCount() {
        const pilferingLevel = gameState.getSkill(SKILLS.PILFERING)?.level || 1;
        let count = 0;
        for (const level of PILFERER_UNLOCK_LEVELS) {
            if (pilferingLevel >= level) count++;
        }
        return count;
    }
}

export const pilferingSkill = new PilferingSkill();
export default pilferingSkill;
